import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileText, Upload, Loader2 } from "lucide-react";
import { resolveImageUrl } from "@/lib/imageUrl";

export default function AdminBlog() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    featured_image_url: "",
    tags: "",
    author: "Junk In Da Truck",
    published: false,
    published_at: "",
    seo_title: "",
    seo_description: "",
  });
  const imageFileRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchItems = useCallback(() => {
    axios.get(`${API}/admin/blog`, { headers }).then(r => {
      setItems(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [headers]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(`${API}/admin/upload-image`, formData, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      timeout: 60000,
    });
    if (res.data.url && res.data.url.startsWith("http")) return res.data.url;
    return `/api/images/${res.data.id}`;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm(p => ({ ...p, featured_image_url: url }));
      toast.success("Featured image uploaded");
    } catch {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const slugify = (text) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const openCreate = () => {
    setEditing(null);
    setForm({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      featured_image_url: "",
      tags: "",
      author: "Junk In Da Truck",
      published: false,
      published_at: "",
      seo_title: "",
      seo_description: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      slug: item.slug || "",
      excerpt: item.excerpt || "",
      content: item.content || "",
      featured_image_url: item.featured_image_url || "",
      tags: (item.tags || []).join(", "),
      author: item.author || "Junk In Da Truck",
      published: !!item.published,
      published_at: item.published_at ? item.published_at : new Date().toISOString(),
      seo_title: item.seo_title || "",
      seo_description: item.seo_description || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) return toast.error("Title is required");
    if (!form.slug) return toast.error("Slug is required");
    if (!form.content) return toast.error("Content is required");

    const payload = {
      title: form.title,
      slug: form.slug,
      excerpt: form.excerpt,
      content: form.content,
      featured_image_url: form.featured_image_url,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      author: form.author,
      published: form.published,
      published_at: form.published ? (form.published_at ? form.published_at : new Date().toISOString()) : null,
      seo_title: form.seo_title,
      seo_description: form.seo_description,
    };

    try {
      if (editing) {
        await axios.put(`${API}/admin/blog/${editing.id}`, payload, { headers });
        toast.success("Blog post updated");
      } else {
        await axios.post(`${API}/admin/blog`, payload, { headers });
        toast.success("Blog post created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save blog post");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/admin/blog/${deleteId}`, { headers });
      toast.success("Blog post deleted");
      setDeleteId(null);
      fetchItems();
    } catch {
      toast.error("Failed to delete blog post");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-blog-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Blog</h1>
          <p className="text-sm text-gray-500">Create and manage blog posts for junkindatruck.com/blog.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] gap-2">
          <Plus className="w-4 h-4" /> Add Post
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-56 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-3">No blog posts yet</p>
            <Button onClick={openCreate} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" /> Add Your First Post
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="overflow-hidden border border-gray-100 hover:shadow-md transition-shadow">
              <div className="relative aspect-[16/10] bg-gray-100">
                {item.featured_image_url ? (
                  <img
                    src={resolveImageUrl(item.featured_image_url)}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <FileText className="w-10 h-10" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button size="icon" variant="secondary" className="h-8 w-8 bg-white/90 hover:bg-white" aria-label={`Edit ${item.title}`} onClick={() => openEdit(item)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="secondary" className="h-8 w-8 bg-white/90 hover:bg-red-50" aria-label={`Delete ${item.title}`} onClick={() => setDeleteId(item.id)}>
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-4 space-y-2">
                <div className="flex flex-wrap gap-1">
                  <Badge variant={item.published ? "default" : "outline"} className={item.published ? "bg-green-600 text-white" : ""}>
                    {item.published ? "Published" : "Draft"}
                  </Badge>
                  {item.tags?.map(tag => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
                </div>
                <h3 className="font-semibold text-sm text-[#0D0D0D] line-clamp-2">{item.title}</h3>
                <p className="text-xs text-gray-500">/{item.slug}</p>
                <p className="text-xs text-gray-500 line-clamp-3">{item.excerpt}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Blog Post" : "Add Blog Post"}</DialogTitle>
              <DialogDescription className="sr-only">Edit the post content and publication settings.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value, slug: editing ? p.slug : slugify(e.target.value) }))} placeholder="Blog post title" />
            </div>
            <div>
              <Label>Slug *</Label>
              <Input value={form.slug} onChange={e => setForm(p => ({ ...p, slug: slugify(e.target.value) }))} placeholder="how-much-does-junk-removal-cost-in-vero-beach" />
            </div>
            <div>
              <Label>Excerpt</Label>
              <Textarea value={form.excerpt} onChange={e => setForm(p => ({ ...p, excerpt: e.target.value }))} placeholder="Short summary for the blog card and SEO preview" rows={2} />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Full blog post content" rows={12} />
            </div>
            <div>
              <Label>Featured Image</Label>
              <div className="flex gap-2 mt-1">
                <Input value={form.featured_image_url} onChange={e => setForm(p => ({ ...p, featured_image_url: e.target.value }))} placeholder="Paste image URL or upload below" className="flex-1" />
                <input type="file" ref={imageFileRef} className="hidden" accept="image/*" onChange={handleFileSelect} />
                <Button type="button" variant="outline" size="icon" onClick={() => imageFileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </Button>
              </div>
              {form.featured_image_url && <img src={resolveImageUrl(form.featured_image_url)} alt="Featured preview" className="mt-2 h-32 w-full object-cover rounded-lg border" loading="lazy" />}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tags</Label>
                <Input value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} placeholder="junk removal, vero beach, pricing" />
              </div>
              <div>
                <Label>Author</Label>
                <Input value={form.author} onChange={e => setForm(p => ({ ...p, author: e.target.value }))} placeholder="Junk In Da Truck" />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium text-[#0D0D0D]">Published</p>
                <p className="text-xs text-gray-500">Turn this on and the post should appear on the live blog immediately.</p>
              </div>
              <Switch checked={form.published} onCheckedChange={checked => setForm(p => ({ ...p, published: checked, published_at: checked ? (p.published_at || new Date().toISOString()) : "" }))} />
            </div>
            <div>
              <Label>SEO Title</Label>
              <Input value={form.seo_title} onChange={e => setForm(p => ({ ...p, seo_title: e.target.value }))} placeholder="Optional custom title tag" />
            </div>
            <div>
              <Label>SEO Description</Label>
              <Textarea value={form.seo_description} onChange={e => setForm(p => ({ ...p, seo_description: e.target.value }))} placeholder="Optional custom meta description" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603]" disabled={uploading}>
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this post. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
